import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useInterval } from 'react-interval-hook';
import { usePageVisibility } from 'react-page-visibility';
import { Link } from 'react-router-dom';

import logo from '../../../img/cc-logo-2022-white.svg';
import {
	loadOpenVisitIds,
	getUnsignedAgreements,
	signAgreement,
	loadCareQuestDashboardCount,
	loadVitalTrackerPatientCount,
} from './api';
import {
	TRACKING_BOARD_REFRESH,
	PATIENT_NOTIFICATION_REFRESH,
} from '../../config/constants';
import AgreementModal from './AgreementModal';
import NotificationCount from './NotificationCount';
import AvailabilityDropdown from './AvailabilityDropdown';
import DropdownMenu from './DropdownMenu';

const NAVBAR_ITEMS = {
	trackingboard: 'Tracking Board',
	previousevisits: 'Previous Visits',
	patientNotifications: 'Patient Notifications',
	adtviewer: 'ADT Viewer',
	acrssearch: 'ACRS Search',
};

const extractActiveRoute = (href) => {
	const [, route] = href.split('#');
	if (route) {
		const [, activeRoute] = route.split('/');
		return activeRoute;
	}
	return '';
};

const NavBar = ({ user, allowPolling, appSocket }) => {
	if (!user || !user.get('loggedIn')) {
		return <></>;
	}
	const [activeRoute, setActivedRoute] = useState(
		extractActiveRoute(document.location.href)
	);
	const [backgroundLoadCount, setBackgroundLoadCount] = useState(0);
	const [agreements, setAgreements] = useState([]);
	const [isSubmittingAgreement, setSubmittingAgreement] = useState(false);
	const [notificationCount, setNotificationCount] = useState(0);
	const [hasRPMDashboard, setHasRPMDashboard] = useState(false);
	const isVisible = usePageVisibility();

	const loadTrackingBoardData = () => {
		if (allowPolling && !isVisible) {
			if (backgroundLoadCount > 4) {
				stopLoadingTrackingBoardData();
				return;
			}
			setBackgroundLoadCount(backgroundLoadCount + 1);
		}
		loadOpenVisitIds(user.get('provider_practice_id')).then(({ data }) => {
			user.set({
				trackingBoardData: data,
			});
		});
	};

	const loadData = () => {
		loadCareQuestDashboardCount(user.get('provider_practice_id')).then(
			({ data }) => {
				setNotificationCount(data.status.length);
			}
		);
	};

	const toggleRefresh = () => {
		if (allowPolling) {
			if (!isVisible) {
				stopLoadingTrackingBoardData();
				stopRefreshDashboardCount();
			} else {
				setBackgroundLoadCount(0);
				loadTrackingBoardData();
				loadData();
				startLoadingTrackingBoardData();
			}
		}
	};

	const {
		start: startRefreshDashboardCount,
		stop: stopRefreshDashboardCount,
		// isActive: isRefreshDashboardCountActive,
	} = useInterval(loadData, PATIENT_NOTIFICATION_REFRESH, {
		autoStart: false,
		immediate: true,
		selfCorrecting: false,
	});
	const {
		start: startLoadingTrackingBoardData,
		stop: stopLoadingTrackingBoardData,
		// isActive: isLoadingTrackingBoardDataActive,
	} = useInterval(loadTrackingBoardData, TRACKING_BOARD_REFRESH, {
		autoStart: false,
		immediate: true,
		selfCorrecting: false,
	});

	useEffect(() => {
		if (allowPolling && isVisible) {
			loadData();
			startLoadingTrackingBoardData();
			startRefreshDashboardCount();
		}
		getUnsignedAgreements(user.get('id')).then(({ data }) => {
			if (
				data &&
				Array.isArray(data.agreements) &&
				data.agreements.length > 0
			) {
				setAgreements(data.agreements);
			}
		});
		loadVitalTrackerPatientCount(user.get('id')).then(({ data }) => {
			setHasRPMDashboard(Array.isArray(data) && data.length > 0);
		});
	}, []);

	useEffect(() => {
		toggleRefresh();
	}, [isVisible]);

	const handleLinkClick = ({ currentTarget }) => {
		if (currentTarget.href && currentTarget.href.indexOf('#') >= 0) {
			setActivedRoute(extractActiveRoute(currentTarget.href));
		}
	};

	const handleCancelAgreement = () => {
		window.location = '#logout';
	};

	const handleAcceptAgreement = async (agreementId) => {
		setSubmittingAgreement(true);
		try {
			// const result =
			await signAgreement(user.id, agreementId);
			// console.log(result)
			const { data } = await getUnsignedAgreements(user.get('id'));
			if (data) {
				if (Array.isArray(data.agreements) && data.agreements.length > 0) {
					setAgreements(data.agreements);
				} else if (data.agreements.all_agreements_signed) {
					setAgreements([]);
				}
			}
			setSubmittingAgreement(false);
		} catch (err) {
			setSubmittingAgreement(false);
		}
	};

	const { provider_practice = {}, appointment_only_ind } = user.toJSON();
	const allowOnDemand = !appointment_only_ind;
	const { practice_oid, organization_oid, care_campaign_ind } =
		provider_practice;

	return (
		<nav className="navbar topnavbar" role="navigation">
			<AgreementModal
				isOpen={agreements.length > 0}
				onCancel={handleCancelAgreement}
				agreements={agreements}
				onAcceptAgreement={handleAcceptAgreement}
				isSubmittingAgreement={isSubmittingAgreement}
			/>
			<div className="navbar-header">
				<button
					type="button"
					className="navbar-toggle collapsed"
					data-toggle="collapse"
					data-target="#bs-example-navbar-collapse-1"
					aria-expanded="false"
				>
					<span className="sr-only">Toggle navigation</span>
					<span className="icon-bar"></span>
					<span className="icon-bar"></span>
					<span className="icon-bar"></span>
				</button>
				<Link className="navbar-brand" to="/">
					<img alt="Brand" src={logo} />
				</Link>
			</div>
			<div className="nav-wrapper collapse navbar-collapse">
				<ul className="nav navbar-nav collapsed-functions">
					<li className="dropdown dropdown-lg">
						<a
							href="#"
							className="dropdown-toggle"
							data-toggle="dropdown"
							role="button"
							aria-haspopup="true"
							aria-expanded="false"
						>
							{NAVBAR_ITEMS[activeRoute]}
							<span className="caret"></span>
						</a>
						<ul className="dropdown-menu">
							<li
								className={cn({
									open: ['', 'trackingboard'].includes(activeRoute),
								})}
							>
								<Link to="/trackingboard" onClick={handleLinkClick}>
									Tracking Board
								</Link>
							</li>
							<li className={cn({ open: activeRoute === 'previousevisits' })}>
								<Link to="/previousevisits" onClick={handleLinkClick}>
									Previous Visits
								</Link>
							</li>
							<li
								className={cn({
									open: activeRoute === 'patientNotifications',
								})}
							>
								<Link to="/patientNotifications" onClick={handleLinkClick}>
									Patient Notifications{' '}
									{allowPolling && (
										<NotificationCount notificationCount={notificationCount} />
									)}
								</Link>
							</li>
							{(practice_oid || organization_oid) && (
								<li className={cn({ open: activeRoute === 'adtviewer' })}>
									<Link to="/adtviewer" onClick={handleLinkClick}>
										ADT Viewer
										{/* {' '}
											<AdtNotificationCount providerId={id} /> */}
									</Link>
								</li>
							)}
							{(practice_oid || organization_oid) && (
								<li className={cn({ open: activeRoute === 'acrssearch' })}>
									<Link to="/acrssearch" onClick={handleLinkClick}>
										ACRS Search
									</Link>
								</li>
							)}
						</ul>
					</li>
				</ul>
				<ul className="nav navbar-nav expanded-functions">
					<li
						className={cn({
							open: ['', 'trackingboard'].includes(activeRoute),
						})}
					>
						<Link to="/trackingboard" onClick={handleLinkClick}>
							Tracking Board
						</Link>
					</li>
					<li className={cn({ open: activeRoute === 'previousevisits' })}>
						<Link to="/previousevisits" onClick={handleLinkClick}>
							Previous Visits
						</Link>
					</li>
					<li className="dropdown">
						<a
							href="#"
							className="dropdown-toggle"
							data-toggle="dropdown"
							role="button"
							aria-haspopup="true"
							aria-expanded="false"
						>
							Notifications <span className="caret" />
						</a>
						<ul className="dropdown-menu">
							<li>
								<Link to="/patientNotifications" onClick={handleLinkClick}>
									New{' '}
									{allowPolling && (
										<NotificationCount notificationCount={notificationCount} />
									)}
								</Link>
							</li>
							<li>
								<Link
									to="/patientNotifications/acknowledged"
									onClick={handleLinkClick}
								>
									Acknowledged
								</Link>
							</li>
						</ul>
						{/* <li className={cn({ open: activeRoute === 'acknowledgedNotifications' })}>
								<Link to="/acknowledgedNotifications" onClick={this.handleLinkClick}>
									Patient Notifications{' '}
									{allowPolling && (
										<NotificationCount notificationCount={notificationCount} />
									)}
								</Link>
							</li> */}
					</li>
					{(practice_oid || organization_oid) && (
						<React.Fragment>
							<li className="dropdown">
								<a
									href="#"
									className="dropdown-toggle"
									data-toggle="dropdown"
									role="button"
									aria-haspopup="true"
									aria-expanded="false"
								>
									ADT <span className="caret" />
								</a>
								<ul className="dropdown-menu">
									<li>
										<Link to="/adtviewer" onClick={handleLinkClick}>
											Viewer
										</Link>
									</li>
									<li>
										<Link to="/patientsearch" onClick={handleLinkClick}>
											Patient Search
										</Link>
									</li>
								</ul>
							</li>
							<li className={cn({ open: activeRoute === 'acrssearch' })}>
								<Link to="/acrssearch" onClick={handleLinkClick}>
									ACRS Search
								</Link>
							</li>
						</React.Fragment>
					)}
					{Boolean(care_campaign_ind) && (
						<li className={cn({ open: activeRoute === 'campaigns' })}>
							<Link to="/campaigns" onClick={handleLinkClick}>
								Outreach Campaigns
							</Link>
						</li>
					)}
					{hasRPMDashboard && (
						<li className={cn({ open: activeRoute === 'rpmdata' })}>
							<Link to="/rpmdata" onClick={handleLinkClick}>
								RPM Data
							</Link>
						</li>
					)}
				</ul>
				<ul className="nav navbar-nav navbar-right">
					{allowOnDemand && <AvailabilityDropdown user={user} />}
					<DropdownMenu user={user} appSocket={appSocket} />
				</ul>
			</div>
		</nav>
	);
};

NavBar.propTypes = {
	user: PropTypes.object.isRequired,
	allowPolling: PropTypes.bool,
	appSocket: PropTypes.object.isRequired,
};

export default NavBar;
